import { useState, useEffect, useCallback } from 'react';
import BlogEditor from '../components/BlogEditor';
import { apiService } from '../services/apiService';
import { useBlogCategories } from '../hooks/useBlogCategories';

const API = process.env.REACT_APP_API_URL || '/api';
const getImgUrl = (p) => !p ? null : p.startsWith('http') ? p : `${API.replace('/api', '')}${p.startsWith('/') ? p : '/' + p}`;

// Icônes SVG intégrées
const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const EditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const TagIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
    <line x1="7" y1="7" x2="7.01" y2="7"></line>
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

export default function BlogManagement() {
  const { categories } = useBlogCategories();
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState('list');
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all | published | draft
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '100', status: filterStatus });
      const data = await apiService.makeRequest(`/blog/admin/posts?${params}`);
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Erreur lors du chargement des articles:', err);
      setError(err.message || 'Impossible de charger les articles');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const filteredPosts = posts.filter(post => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q || post.titre.toLowerCase().includes(q) ||
      (post.contenu || '').toLowerCase().includes(q);
    const matchesCategory = filterCategory === 'all' || post.categorie === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleEdit = (post) => {
    setSelectedPost(post);
    setView('form');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      try {
        await apiService.makeRequest(`/blog/admin/posts/${id}`, { method: 'DELETE' });
        await fetchArticles();
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        alert(err.message || 'Erreur lors de la suppression');
      }
    }
  };

  const handleView = (post) => {
    setSelectedPost(post);
    setView('preview');
  };

  const handleSaveSuccess = () => {
    fetchArticles();
    setSelectedPost(null);
    setView('list');
  };

  const handleCancel = () => {
    setSelectedPost(null);
    setView('list');
  };

  const getCategoryText = (categorie) => {
    const cat = categories.find(c => c.valeur === categorie);
    return cat ? cat.libelle : (categorie || '—');
  };

  const getAuthorName = (post) => [post.author_prenom, post.author_nom].filter(Boolean).join(' ');

  // Extraire le texte brut du HTML pour l'aperçu
  const getExcerpt = (html, maxLength = 150) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestion du Blog</h1>
          <p className="text-gray-600">Gérez tous vos articles de blog en un seul endroit</p>
        </header>

        {view === 'list' && (
          <div>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1 relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <SearchIcon />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher un article..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.valeur}>{cat.libelle}</option>
                  ))}
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="published">Publié</option>
                  <option value="draft">Brouillon</option>
                </select>
                <button
                  onClick={() => { setSelectedPost(null); setView('form'); }}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon />
                  Nouvel Article
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Chargement...</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredPosts.map(post => (
                    <div key={post.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex gap-4">
                        {post.image_principale && (
                          <img
                            src={getImgUrl(post.image_principale)}
                            alt={post.titre}
                            className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">{post.titre}</h3>
                          <p className="text-gray-600 mb-3">{post.resume || getExcerpt(post.contenu)}</p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-sm rounded">
                              <TagIcon />
                              {getCategoryText(post.categorie)}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            {getAuthorName(post) && (
                              <span className="flex items-center gap-1">
                                <UserIcon />
                                {getAuthorName(post)}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <CalendarIcon />
                              {new Date(post.created_at).toLocaleDateString('fr-FR')}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${post.is_published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {post.is_published ? 'Publié' : 'Brouillon'}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleView(post)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors h-fit"
                            title="Voir"
                          >
                            <EyeIcon />
                          </button>
                          <button
                            onClick={() => handleEdit(post)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors h-fit"
                            title="Modifier"
                          >
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors h-fit"
                            title="Supprimer"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && !error && filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Aucun article trouvé</p>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'form' && (
          <BlogEditor
               article={selectedPost}
               onSave={handleSaveSuccess}
               onCancel={handleCancel}
          />
        )}

        {view === 'preview' && selectedPost && (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="mb-6">
              <button
                onClick={() => setView('list')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Retour à la liste
              </button>
            </div>

            <article className="prose max-w-none">
              {selectedPost.image_principale && (
                <img
                  src={getImgUrl(selectedPost.image_principale)}
                  alt={selectedPost.titre}
                  className="w-full h-96 object-cover rounded-lg mb-6"
                />
              )}

              <h1 className="text-4xl font-bold text-gray-900 mb-4">{selectedPost.titre}</h1>

              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-6 pb-6 border-b">
                {getAuthorName(selectedPost) && (
                  <span className="flex items-center gap-1">
                    <UserIcon />
                    {getAuthorName(selectedPost)}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <CalendarIcon />
                  {new Date(selectedPost.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${selectedPost.is_published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {selectedPost.is_published ? 'Publié' : 'Brouillon'}
                </span>
              </div>

              <div className="flex gap-2 mb-6">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                  <TagIcon />
                  {getCategoryText(selectedPost.categorie)}
                </span>
              </div>

              {selectedPost.audio_url && (
                <audio controls src={getImgUrl(selectedPost.audio_url)} className="w-full mb-6" />
              )}

              <div
                className="text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedPost.contenu }}
              />
            </article>

            <div className="mt-8 pt-6 border-t flex gap-4">
              <button
                onClick={() => handleEdit(selectedPost)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <EditIcon />
                Modifier
              </button>
              <button
                onClick={() => handleDelete(selectedPost.id)}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <TrashIcon />
                Supprimer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
